import type { SxProps, Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

export const settingsPagePaddingSx: SystemStyleObject<Theme> = {
  px: { xs: 2, sm: 3, md: 10, lg: 14 },
  py: { xs: 2, md: 2.5 },
  maxWidth: "min(100%, 1680px)",
  mx: "auto",
  boxSizing: "border-box"
};

export const settingsSectionGap = 2.5;

/** Indents for settings section headings and nested content. */
export const settingsSubmenuPl = { xs: 1.5, sm: 2 } as const;

/** Extra indent for controls under a settings subsection title. */
export const settingsSubmenuContentPl = { xs: 1.5, sm: 2 } as const;

export const settingsSectionHeadingSx: SxProps<Theme> = {
  fontWeight: 500,
  letterSpacing: 0
};

export const dangerZoneConfirmFieldWidthPx: number = 200;
export const dangerZoneActionButtonWidthPx: number = 280;

/** Outer panel for danger-zone content (Dev Tools rows + workstation delete block). */
export function settingsDangerZonePanelSx(
  theme: Theme
): SystemStyleObject<Theme> {
  return {
    p: "28px",
    borderRadius: 2,
    width: "100%",
    boxSizing: "border-box",
    bgcolor: theme.palette.error.light,
    border: `1px solid ${theme.palette.error.main}`
  };
}

export const settingsUtilityLabelColumnWidthPx: number = 280;
export const settingsWideButtonWidthPx: number = 280;

/** Body text size for settings form controls (selects, inputs) in the preview settings area. */
export const settingsControlFontSize = "0.8125rem";

/** Minimum height for settings row controls (px). */
export const settingsInputMinHeightPx = 40;

/** Helper text under label + control rows (bins maintenance, workstation overview, etc.). */
export const settingsOverviewHelperFontSize = "0.75rem";

/** Fixed label column width for settings field rows. */
export const settingsOverviewLabelColumnPx = 160;

export const settingsOverviewLabelControlGap = 0.75;

/** Shorter gap between label and control on narrow rows. */
export const settingsOverviewLabelControlGapTight = 0.5;

/** Max width for controls in settings field rows on wide viewports. */
export const settingsOverviewFormControlMaxWidthPx = 360;

/** Label + control grid for overview / labeled field rows (sm+). */
export const settingsOverviewRowGridTemplateColumns = {
  xs: "1fr",
  sm: `${settingsOverviewLabelColumnPx}px minmax(0, ${settingsOverviewFormControlMaxWidthPx}px)`
} as const;

export const settingsOverviewLabelControlColumnGap = {
  xs: 1,
  sm: settingsOverviewLabelControlGapTight,
  md: settingsOverviewLabelControlGapTight,
  lg: settingsOverviewLabelControlGap
} as const;

/** Site / FC / workstation dropdowns on settings pages (wider than overview form controls). */
export const settingsSiteSelectMaxWidthPx = 440;

/**
 * Outlined Select styling shared across preview settings (general + workstation).
 * Matches dense MUI small controls aligned with overview rows.
 */
export const settingsSelectSx: SxProps<Theme> = {
  minHeight: settingsInputMinHeightPx,
  fontSize: settingsControlFontSize,
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: settingsInputMinHeightPx,
    boxSizing: "border-box",
    fontSize: settingsControlFontSize,
    lineHeight: 1.35,
    paddingLeft: "14px"
  }
};
